import React from "react";
import { useForm, router } from "@inertiajs/react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { parseAddressFeatureComplete, type AddressFeature } from "../../lib/addressUtils";
import {
  PRACTICE_LOCATIONS,
  PARKING_TYPES,
  PREFERRED_CONTACTS,
  APPOINTMENT_METHODS,
  NEW_PATIENTS_POLICIES,
  SESSION_DURATIONS,
  PATIENT_TYPES,
  PRACTICE_MODES,
  PAYMENT_METHODS,
  COORDINATED_PRACTICE_TYPES,
  SPECIALTIES,
  TECHNIQUES,
  EQUIPMENT,
  ACCESSIBILITY,
  LANGUAGES,
  SPORTS,
  OTHER_ACTIVITIES,
} from "../../lib/constants";

interface Props {
  physio: {
    id: string;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    zip?: string;
    lat: number;
    lng: number;
    specialties: string[];
    isCertified: boolean;
    phone: string;
    body_zones: string[];
    accessibility: string[];
    languages: string[];
    equipment: string[];
    payment_type: string;
    appointment_mode: string;
    booking_link: string;
    practice_locations: string[];
    home_visit_areas: string;
    professional_address: string;
    practice_name: string;
    practitioners_count: number;
    parking_type: string;
    parking_types: string[];
    professional_email: string;
    professional_website: string;
    preferred_contact: string;
    preferred_contact_details: string;
    appointment_methods: string[];
    online_booking_link: string;
    secretariat_hours: string;
    new_patients_policy: string;
    session_duration: string;
    patient_types: string[];
    waiting_list: boolean;
    waiting_list_delay: string;
    practice_mode: string;
    practice_modes: string[];
    honorary_details: string;
    unconventional_practice_description: string;
    payment_methods: string[];
    coordinated_practice: string[];
    coordinated_practice_details: string;
    techniques: string[];
    other_techniques: string;
    sports: string[];
    secondary_address: string;
    cv_url: string;
    diploma_year: number | null;
    diploma_place: string;
    diploma_extra_1: string;
    diploma_extra_2: string;
    association_name: string;
    other_activities: string[];
    other_activities_details: string;
    rpps_number: string;
    adeli_number: string;
    publications: string;
    training_courses: string;
    other_equipment: string;
    other_languages: string;
    other_sports: string;
  };
  errors?: Record<string, string[]>;
}

type FormData = {
  first_name: string;
  last_name: string;
  address: string;
  city: string;
  zip?: string;
  latitude: number | null;
  longitude: number | null;
  phone: string;
  certified: boolean;
  specialties: string[];
  body_zones: string[];
  accessibility: string[];
  languages: string[];
  equipment: string[];
  payment_type: string;
  appointment_mode: string;
  booking_link: string;
  practice_locations: string[];
  home_visit_areas: string;
  professional_address: string;
  practice_name: string;
  practitioners_count: number;
  parking_type: string;
  parking_types: string[];
  professional_email: string;
  professional_website: string;
  preferred_contact: string;
  preferred_contact_details: string;
  appointment_methods: string[];
  online_booking_link: string;
  secretariat_hours: string;
  new_patients_policy: string;
  session_duration: string;
  patient_types: string[];
  waiting_list: boolean;
  waiting_list_delay: string;
  practice_mode: string;
  practice_modes: string[];
  honorary_details: string;
  unconventional_practice_description: string;
  payment_methods: string[];
  coordinated_practice: string[];
  coordinated_practice_details: string;
  techniques: string[];
  other_techniques: string;
  sports: string[];
  secondary_address: string;
  cv_url: string;
  diploma_year: string;
  diploma_place: string;
  diploma_extra_1: string;
  diploma_extra_2: string;
  association_name: string;
  other_activities: string[];
  other_activities_details: string;
  rpps_number: string;
  adeli_number: string;
  publications: string;
  training_courses: string;
  other_equipment: string;
  other_languages: string;
  other_sports: string;
  cv: File | null;
};

type ArrayField = 'specialties' | 'body_zones' | 'accessibility' | 'languages' | 'equipment' | 
                 'practice_locations' | 'patient_types' | 'payment_methods' | 'techniques' | 
                 'appointment_methods' | 'coordinated_practice' | 'sports' | 'parking_types' | 'practice_modes' | 'other_activities';

export default function Edit({ physio, errors }: Props) {
  const { data, setData, processing } = useForm<FormData>({
    first_name: physio.firstName,
    last_name: physio.lastName,
    address: physio.address || '',
    city: physio.city || '',
    zip: physio.zip || '',
    latitude: physio.lat || null,
    longitude: physio.lng || null,
    phone: physio.phone || '',
    certified: physio.isCertified,
    specialties: physio.specialties,
    body_zones: physio.body_zones,
    accessibility: physio.accessibility,
    languages: physio.languages,
    equipment: physio.equipment,
    payment_type: physio.payment_type,
    appointment_mode: physio.appointment_mode,
    booking_link: physio.booking_link,
    practice_locations: physio.practice_locations || [],
    home_visit_areas: physio.home_visit_areas || "",
    professional_address: physio.professional_address || "",
    practice_name: physio.practice_name || "",
    practitioners_count: physio.practitioners_count || 1,
    parking_type: physio.parking_type || "",
    parking_types: physio.parking_types || [],
    professional_email: physio.professional_email || "",
    professional_website: physio.professional_website || "",
    preferred_contact: physio.preferred_contact || "",
    preferred_contact_details: physio.preferred_contact_details || "",
    appointment_methods: physio.appointment_methods || [],
    online_booking_link: physio.online_booking_link || "",
    secretariat_hours: physio.secretariat_hours || "",
    new_patients_policy: physio.new_patients_policy || "",
    session_duration: physio.session_duration || "",
    patient_types: physio.patient_types || [],
    waiting_list: physio.waiting_list || false,
    waiting_list_delay: physio.waiting_list_delay || "",
    practice_mode: physio.practice_mode || "",
    practice_modes: physio.practice_modes || [],
    honorary_details: physio.honorary_details || "",
    unconventional_practice_description: physio.unconventional_practice_description || "",
    payment_methods: physio.payment_methods || [],
    coordinated_practice: physio.coordinated_practice || [],
    coordinated_practice_details: physio.coordinated_practice_details || "",
    techniques: physio.techniques || [],
    other_techniques: physio.other_techniques || "",
    sports: physio.sports || [],
    secondary_address: physio.secondary_address || "",
    cv_url: physio.cv_url || "",
    diploma_year: (physio.diploma_year ?? "").toString(),
    diploma_place: physio.diploma_place || "",
    diploma_extra_1: physio.diploma_extra_1 || "",
    diploma_extra_2: physio.diploma_extra_2 || "",
    association_name: physio.association_name || "",
    other_activities: physio.other_activities || [],
    other_activities_details: physio.other_activities_details || "",
    rpps_number: physio.rpps_number || "",
    adeli_number: physio.adeli_number || "",
    publications: physio.publications || "",
    training_courses: physio.training_courses || "",
    other_equipment: physio.other_equipment || "",
    other_languages: physio.other_languages || "",
    other_sports: physio.other_sports || "",
    cv: null
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let finalAddress = data.address || '';
    let finalCity = data.city || '';
    let finalZip = data.zip || '';
    let finalLatitude = data.latitude !== null && data.latitude !== undefined ? Number(data.latitude) : null;
    let finalLongitude = data.longitude !== null && data.longitude !== undefined ? Number(data.longitude) : null;
    
    if (finalLatitude !== null && (!isFinite(finalLatitude) || isNaN(finalLatitude))) {
      finalLatitude = null;
    }
    if (finalLongitude !== null && (!isFinite(finalLongitude) || isNaN(finalLongitude))) {
      finalLongitude = null;
    }
    
    const hasValidCoordinates = finalLatitude !== null && finalLongitude !== null && 
                                 isFinite(finalLatitude) && isFinite(finalLongitude);
    const hasCityAndZip = !!(finalCity && finalZip);
    
    if (finalAddress && !hasValidCoordinates && !hasCityAndZip) {
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(finalAddress)}&limit=1`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const apiData = await response.json();
        
        if (apiData.features && apiData.features.length > 0) {
          const feature = apiData.features[0] as AddressFeature;
          const parsed = await parseAddressFeatureComplete(feature);
          
          finalCity = parsed.city || finalCity;
          finalZip = parsed.zip || finalZip;
          finalLatitude = parsed.latitude;
          finalLongitude = parsed.longitude;
        }
      } catch (error) {
        console.error('Erreur lors de la géocodification de l\'adresse:', error);
      }
    }
    
    const { accessibility, ...dataWithoutAccessibility } = data;
    const formDataToSend = {
      ...dataWithoutAccessibility,
      address: finalAddress,
      city: finalCity,
      zip: finalZip,
      latitude: finalLatitude,
      longitude: finalLongitude,
      accessibility: accessibility || []
    };
    
    setData('address', finalAddress);
    setData('city', finalCity);
    setData('zip', finalZip);
    setData('latitude', finalLatitude);
    setData('longitude', finalLongitude);
    
    router.put(`/physiotherapists/${physio.id}`, {
      physiotherapist: formDataToSend
    }, {
      preserveScroll: true
    });
  };

  const toggleArrayItem = (field: ArrayField, value: string) => {
    const current = data[field];
    const updated = current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];
    setData(field, updated);
  };

  const renderCheckboxGroup = (title: string, items: readonly string[], field: ArrayField) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {title}
      </label>
      <div className="flex flex-wrap gap-2">
        {items.map(item => (
          <Badge
            key={item}
            variant={data[field].includes(item) ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => toggleArrayItem(field, item)}
          >
            {item}
          </Badge>
        ))}
      </div>
    </div>
  );

  const renderSelect = (title: string, value: string, options: readonly string[], field: keyof FormData) => (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        {title}
      </label>
      <Select value={value} onValueChange={(newValue) => setData(field, newValue)}>
        <SelectTrigger className="mt-1">
          <SelectValue placeholder="Sélectionner..." />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Modifier votre profil
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {errors && Object.keys(errors).length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {Object.values(errors).flat().map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </AlertDescription>
                </Alert>
              )}

              <Accordion type="multiple" className="w-full">
                {/* Informations de base */}
                <AccordionItem value="basic">
                  <AccordionTrigger>Informations de base</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Prénom
                        </label>
                        <Input
                          value={data.first_name}
                          onChange={e => setData("first_name", e.target.value)}
                          className={errors?.first_name ? "border-red-500" : ""}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Nom
                        </label>
                        <Input
                          value={data.last_name}
                          onChange={e => setData("last_name", e.target.value)}
                          className={errors?.last_name ? "border-red-500" : ""}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Téléphone professionnel (visible par tous)
                        </label>
                        <Input
                          type="tel"
                          value={data.phone}
                          onChange={e => setData("phone", e.target.value)}
                          className={errors?.phone ? "border-red-500" : ""}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email professionnel (visible par tous)
                        </label>
                        <Input
                          type="email"
                          value={data.professional_email}
                          onChange={e => setData("professional_email", e.target.value)}
                          className={errors?.professional_email ? "border-red-500" : ""}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Site internet professionnel
                        </label>
                        <Input
                          type="url"
                          value={data.professional_website}
                          onChange={e => setData("professional_website", e.target.value)}
                          placeholder="https://..."
                          className={errors?.professional_website ? "border-red-500" : ""}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          N° RPPS
                        </label>
                        <Input
                          value={data.rpps_number}
                          onChange={e => setData("rpps_number", e.target.value)}
                          placeholder="Ex: 10001234567"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          N° ADELI
                        </label>
                        <Input
                          value={data.adeli_number}
                          onChange={e => setData("adeli_number", e.target.value)}
                          placeholder="Ex: 751234567"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Année du diplôme
                        </label>
                        <Input
                          type="number"
                          value={data.diploma_year}
                          onChange={e => setData("diploma_year", e.target.value)}
                          placeholder="Ex: 2015"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Lieu du diplôme
                        </label>
                        <Input
                          value={data.diploma_place}
                          onChange={e => setData("diploma_place", e.target.value)}
                          placeholder="Ex: Paris"
                        />
                      </div>
                      {/* Champ certifié masqué pour les kinés (réservé admin) */}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Lieu d'exercice */}
                <AccordionItem value="practice">
                  <AccordionTrigger>Lieu d'exercice</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderCheckboxGroup("Lieu d'exercice", PRACTICE_LOCATIONS, "practice_locations")}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Adresse *
                        </label>
                        <AddressAutocomplete
                          value={data.address || ''}
                          onChange={(address, city, zip, lat, lng) => {
                            setData("address", address || '');
                            
                            if (city) {
                              setData("city", city);
                            }
                            if (zip) {
                              setData("zip", zip);
                            }
                            
                            if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
                              const latNum = Number(lat);
                              const lngNum = Number(lng);
                              if (!isNaN(latNum) && isFinite(latNum) && !isNaN(lngNum) && isFinite(lngNum)) {
                                setData("latitude", latNum);
                                setData("longitude", lngNum);
                              } else {
                                if (!data.latitude || !data.longitude) {
                                  setData("latitude", null);
                                  setData("longitude", null);
                                }
                              }
                            } else if (lat === null && lng === null && !city && !zip) {
                              if (!data.latitude || !data.longitude) {
                                setData("latitude", null);
                                setData("longitude", null);
                              }
                            }
                          }}
                          placeholder="Rechercher une adresse..."
                          className={errors?.address ? "border-red-500" : ""}
                        />
                        {errors?.address && (
                          <p className="text-sm text-red-500 mt-1">{errors.address[0]}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          La ville et le code postal se rempliront automatiquement
                        </p>
                        {data.latitude != null && data.longitude != null && 
                         typeof data.latitude === 'number' && typeof data.longitude === 'number' &&
                         isFinite(data.latitude) && isFinite(data.longitude) && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Coordonnées GPS définies: {data.latitude.toFixed(6)}, {data.longitude.toFixed(6)}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ville
                          </label>
                          <Input
                            value={data.city || ''}
                            onChange={e => setData("city", e.target.value)}
                            placeholder="Ville"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Code postal
                          </label>
                          <Input
                            value={data.zip || ''}
                            onChange={e => setData("zip", e.target.value)}
                            placeholder="Code postal"
                          />
                        </div>
                      </div>
                      
                      {data.practice_locations.includes("Domicile") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Secteurs desservis (rues, quartiers)
                          </label>
                          <Textarea
                            value={data.home_visit_areas}
                            onChange={e => setData("home_visit_areas", e.target.value)}
                            placeholder="Précisez les secteurs desservis..."
                            rows={3}
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Nom du cabinet
                        </label>
                        <Input
                          value={data.practice_name}
                          onChange={e => setData("practice_name", e.target.value)}
                          placeholder="Nom du cabinet"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Nombre de praticiens du cabinet
                        </label>
                        <Select value={data.practitioners_count.toString()} onValueChange={(value) => setData("practitioners_count", parseInt(value))}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 20}, (_, i) => i + 1).map(num => (
                              <SelectItem key={num} value={num.toString()}>
                                {num}
                              </SelectItem>
                            ))}
                            <SelectItem value="21">+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Accessibilité et stationnement */}
                <AccordionItem value="accessibility">
                  <AccordionTrigger>Accessibilité et stationnement</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderCheckboxGroup("Accessibilité", ACCESSIBILITY, "accessibility")}

                      {/* Types de stationnement */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Types de stationnement
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {PARKING_TYPES.map(item => (
                            <Badge
                              key={item}
                              variant={data.parking_types.includes(item) ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => toggleArrayItem('parking_types', item)}
                            >
                              {item}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Prise de rendez-vous et Contact */}
                <AccordionItem value="contact">
                  <AccordionTrigger>Prise de rendez-vous et Contact</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderCheckboxGroup("Modes de prise de rendez-vous", APPOINTMENT_METHODS, "appointment_methods")}
                      
                      {data.appointment_methods.includes("En ligne") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Lien de réservation en ligne
                          </label>
                          <Input
                            type="url"
                            value={data.online_booking_link}
                            onChange={e => setData("online_booking_link", e.target.value)}
                            placeholder="https://..."
                          />
                        </div>
                      )}

                      {renderSelect("Contact préférentiel", data.preferred_contact, PREFERRED_CONTACTS, "preferred_contact")}
                      
                      {data.preferred_contact === "Autres" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Précision contact préférentiel
                          </label>
                          <Input
                            value={data.preferred_contact_details}
                            onChange={e => setData("preferred_contact_details", e.target.value)}
                            placeholder="Précisez..."
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Informations complémentaires (horaires secrétariat,…)
                        </label>
                        <Textarea
                          value={data.secretariat_hours}
                          onChange={e => setData("secretariat_hours", e.target.value)}
                          placeholder="Lundi 9h-12h, 14h-18h..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Nouveaux patients et durée */}
                <AccordionItem value="patients">
                  <AccordionTrigger>Nouveaux patients et durée</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderSelect("Nouveaux patients", data.new_patients_policy, NEW_PATIENTS_POLICIES, "new_patients_policy")}
                      
                      {renderSelect("Durée des séances", data.session_duration, SESSION_DURATIONS, "session_duration")}
                      
                      {renderCheckboxGroup("Type de patientèle", PATIENT_TYPES, "patient_types")}
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="waiting_list"
                          checked={data.waiting_list}
                          onCheckedChange={(checked) => setData("waiting_list", checked === true)}
                        />
                        <label htmlFor="waiting_list" className="text-sm font-medium text-gray-700">
                          Liste d'attente
                        </label>
                      </div>
                      
                      {data.waiting_list && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Délai moyen
                          </label>
                          <Input
                            value={data.waiting_list_delay}
                            onChange={e => setData("waiting_list_delay", e.target.value)}
                            placeholder="ex: 2 semaines"
                          />
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Mode d'exercice et règlement */}
                <AccordionItem value="practice_mode">
                  <AccordionTrigger>Mode d'exercice et règlement</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {/* Modes d'exercice */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Modes d'exercice
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {PRACTICE_MODES.map(item => (
                            <Badge
                              key={item}
                              variant={data.practice_modes.includes(item) ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => toggleArrayItem('practice_modes', item)}
                            >
                              {item}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {data.practice_modes.includes("Dépassement d'honoraires (DE, HN)") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Précision dépassement d'honoraires
                          </label>
                          <Textarea
                            value={data.honorary_details}
                            onChange={e => setData("honorary_details", e.target.value)}
                            placeholder="Précisez les conditions..."
                            rows={3}
                          />
                        </div>
                      )}

                      {data.practice_modes.includes("Activité déconventionnée") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Décrivez votre pratique déconventionnée
                          </label>
                          <Textarea
                            value={data.unconventional_practice_description}
                            onChange={e => setData("unconventional_practice_description", e.target.value)}
                            placeholder="Décrivez votre activité déconventionnée, vos tarifs, vos prestations..."
                            rows={4}
                          />
                        </div>
                      )}

                      {renderCheckboxGroup("Type de règlement", PAYMENT_METHODS, "payment_methods")}
                      
                      {renderCheckboxGroup("Exercice coordonné", COORDINATED_PRACTICE_TYPES, "coordinated_practice")}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Précision exercice coordonné
                        </label>
                        <Input
                          value={data.coordinated_practice_details}
                          onChange={e => setData("coordinated_practice_details", e.target.value)}
                          placeholder="Précisez..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Associations
                        </label>
                        <Textarea
                          value={data.association_name}
                          onChange={e => setData("association_name", e.target.value)}
                          placeholder="Ex: Membre de la SFMKS, ..."
                          rows={2}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Publications
                        </label>
                        <Textarea
                          value={data.publications}
                          onChange={e => setData("publications", e.target.value)}
                          placeholder="Listez vos publications..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Spécialités et techniques */}
                <AccordionItem value="specialties">
                  <AccordionTrigger>Spécialités et techniques</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderCheckboxGroup("Spécialités", SPECIALTIES, "specialties")}
                      {renderCheckboxGroup("Techniques", TECHNIQUES, "techniques")}
                      
                      {data.techniques.includes("Autres") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Précisez les autres techniques
                          </label>
                          <Input
                            value={data.other_techniques}
                            onChange={e => setData("other_techniques", e.target.value)}
                            placeholder="Ex: Fasciathérapie, ..."
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Formations suivies
                        </label>
                        <Textarea
                          value={data.training_courses}
                          onChange={e => setData("training_courses", e.target.value)}
                          placeholder="Listez vos formations suivies..."
                          rows={4}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CV
                        </label>
                        <Input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={e => {
                            const file = e.target.files?.[0] || null;
                            setData("cv", file);
                          }}
                        />
                        {physio.cv_url && (
                          <p className="text-sm text-gray-500 mt-1">
                            <a href={physio.cv_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              CV actuel
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Sports et Equipements */}
                <AccordionItem value="sports_equipment">
                  <AccordionTrigger>Sports et Equipements</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderCheckboxGroup("Sports", SPORTS, "sports")}
                      
                      {data.sports.includes("Autres") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Précisez les autres sports
                          </label>
                          <Input
                            value={data.other_sports}
                            onChange={e => setData("other_sports", e.target.value)}
                            placeholder="Ex: Ski, Escalade, ..."
                          />
                        </div>
                      )}
                      
                      {renderCheckboxGroup("Equipements", EQUIPMENT, "equipment")}
                      
                      {data.equipment.includes("Autres") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Précisez les autres équipements
                          </label>
                          <Input
                            value={data.other_equipment}
                            onChange={e => setData("other_equipment", e.target.value)}
                            placeholder="Ex: Électrostimulation, ..."
                          />
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Langues */}
                <AccordionItem value="languages">
                  <AccordionTrigger>Langues parlées</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderCheckboxGroup("Langues parlées", LANGUAGES, "languages")}
                      
                      {data.languages.includes("Autres") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Précisez les autres langues
                          </label>
                          <Input
                            value={data.other_languages}
                            onChange={e => setData("other_languages", e.target.value)}
                            placeholder="Ex: Portugais, Japonais, ..."
                          />
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Autres activités */}
                <AccordionItem value="other_activities">
                  <AccordionTrigger>Autres activités</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {renderCheckboxGroup("Autres activités", OTHER_ACTIVITIES, "other_activities")}
                      
                      {data.other_activities.includes("Autres") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Précisez les autres activités
                          </label>
                          <Input
                            value={data.other_activities_details}
                            onChange={e => setData("other_activities_details", e.target.value)}
                            placeholder="Précisez..."
                          />
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={processing}
                >
                  {processing ? "Enregistrement..." : "Enregistrer les modifications"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
